import { useState, useCallback, useRef, useEffect } from 'react';
import type { ZodTypeAny, ZodIssue } from '../lib/zodLoader';

/**
 * Form submission status
 */
export type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

/**
 * Validation error map — field name → error message
 */
export type FormValidationErrors = Record<string, string>;

/**
 * Form submission options
 */
export interface FormSubmitOptions<TSchema extends ZodTypeAny = ZodTypeAny> {
  /** Form submission URL */
  url: string;
  /**
   * Optional Zod schema for client-side validation.
   * If provided, data is validated before submission.
   * Invalid data sets validationErrors and does NOT submit.
   *
   * @example
   * const schema = await getBrokerInquirySchema();
   * const { handleSubmit } = useFormSubmit({ url, schema });
   */
  schema?: TSchema;
  /** Success callback - called after successful submission */
  onSuccess?: () => void;
  /** Error callback - called after failed submission */
  onError?: (error: Error) => void;
  /** Conversion tracking label for Google Ads */
  conversionLabel?: string;
  /** Conversion value for Google Ads */
  conversionValue?: number;
  /** GA4 lead event name */
  leadEventName?: string;
  /** Auto-reset form status after success (ms) */
  resetDelay?: number;
  /**
   * Optional ref to the <form> element.
   * When provided, focus is programmatically moved to the first
   * invalid field after a failed validation so keyboard users
   * don't have to hunt for the error.
   */
  formRef?: React.RefObject<HTMLFormElement | null>;
  /**
   * Optional ref to the server-error live region element.
   * When provided, focus is moved to it after a network/server error
   * so screen readers announce the message immediately.
   */
  errorRef?: React.RefObject<HTMLElement | null>;
  /**
   * Optional ref to the success message element.
   * When provided, focus is moved to it after a successful submission
   * so keyboard users receive confirmation without having to Tab.
   */
  successRef?: React.RefObject<HTMLElement | null>;
}

/**
 * Form submission result
 */
export interface FormSubmitResult {
  /** Current submission status */
  status: FormStatus;
  /** Submit handler for form onSubmit */
  handleSubmit: (data: Record<string, string | boolean>) => Promise<void>;
  /** Reset status to idle */
  reset: () => void;
  /** Whether form is currently submitting */
  isSubmitting: boolean;
  /** Whether submission was successful */
  isSuccess: boolean;
  /** Whether submission failed */
  isError: boolean;
  /**
   * Field-level validation errors from Zod schema.
   * Empty object when no errors or no schema provided.
   * Cleared on each new submission attempt.
   *
   * @example
   * {
   *   name: 'Name must be at least 2 characters',
   *   email: 'Please enter a valid email',
   * }
   */
  validationErrors: FormValidationErrors;
  /** Whether there are any validation errors */
  hasValidationErrors: boolean;
}

/**
 * Shared form submission hook with Zod validation, consistent loading,
 * error handling, and success feedback.
 *
 * Features:
 * - Optional Zod schema validation before submission
 * - Field-level validation error map for inline form feedback
 * - Automatic abort on unmount (prevents memory leaks)
 * - Consistent error handling and logging
 * - Optional conversion tracking (Google Ads + GA4)
 * - Auto-reset after success
 * - Type-safe form data handling
 *
 * @example
 * ```tsx
 * const schema = await getBrokerInquirySchema();
 * const { status, handleSubmit, validationErrors, isSubmitting } = useFormSubmit({
 *   url: 'https://readdy.ai/api/form/abc123',
 *   schema,
 *   onSuccess: () => navigate('/thank-you'),
 * });
 *
 * // In JSX:
 * {validationErrors.email && (
 *   <p className="text-red-500 text-xs">{validationErrors.email}</p>
 * )}
 * ```
 */
export function useFormSubmit<TSchema extends ZodTypeAny = ZodTypeAny>(
  options: FormSubmitOptions<TSchema>
): FormSubmitResult {
  const {
    url,
    schema,
    onSuccess,
    onError,
    conversionLabel,
    conversionValue,
    leadEventName,
    resetDelay = 0,
  } = options;

  const [status, setStatus] = useState<FormStatus>('idle');
  const [validationErrors, setValidationErrors] = useState<FormValidationErrors>({});
  const abortControllerRef = useRef<AbortController | null>(null);
  const resetTimeoutRef = useRef<number | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (resetTimeoutRef.current) {
        window.clearTimeout(resetTimeoutRef.current);
      }
    };
  }, []);

  const reset = useCallback(() => {
    setStatus('idle');
    setValidationErrors({});
    if (resetTimeoutRef.current) {
      window.clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }
  }, []);

  const handleSubmit = useCallback(
    async (data: Record<string, string | boolean>) => {
      // Clear previous validation errors
      setValidationErrors({});

      // ── Zod validation (if schema provided) ──────────────────────────────
      if (schema) {
        const result = schema.safeParse(data);
        if (!result.success) {
          const errors: FormValidationErrors = {};
          result.error.errors.forEach((err: ZodIssue) => {
            const field = err.path.join('.');
            if (field && !errors[field]) {
              errors[field] = err.message;
            }
          });
          setValidationErrors(errors);

          // Move focus to the first invalid field so keyboard users land on the error
          const firstField = Object.keys(errors)[0];
          if (firstField && options.formRef?.current) {
            const el =
              options.formRef.current.querySelector<HTMLElement>(`[name="${firstField}"]`) ??
              options.formRef.current.querySelector<HTMLElement>(`#${firstField}`);
            if (el) {
              el.focus();
            }
          }

          return; // Do NOT submit — show inline errors
        }
      }

      // Abort any previous in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      setStatus('submitting');

      try {
        // Convert data to URLSearchParams (application/x-www-form-urlencoded)
        const formBody = new URLSearchParams();
        Object.entries(data).forEach(([key, value]) => {
          formBody.append(key, String(value));
        });

        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: formBody.toString(),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          throw new Error(`Form submission failed with status ${response.status}`);
        }

        setStatus('success');

        // Move focus to success message so keyboard users get confirmation
        requestAnimationFrame(() => {
          if (options.successRef?.current) {
            options.successRef.current.focus();
          }
        });

        // Google Ads conversion tracking
        if (conversionLabel && typeof (window as Window & { trackConversion?: (l: string, v: number) => void }).trackConversion === 'function') {
          (window as Window & { trackConversion?: (l: string, v: number) => void }).trackConversion!(conversionLabel, conversionValue ?? 1);
        }

        // GA4 lead event tracking
        if (leadEventName && typeof (window as Window & { trackLead?: (n: string) => void }).trackLead === 'function') {
          (window as Window & { trackLead?: (n: string) => void }).trackLead!(leadEventName);
        }

        if (onSuccess) onSuccess();

        if (resetDelay > 0) {
          resetTimeoutRef.current = window.setTimeout(() => {
            setStatus('idle');
          }, resetDelay);
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;

        setStatus('error');

        // Move focus to the server-error live region so screen readers announce it
        requestAnimationFrame(() => {
          if (options.errorRef?.current) {
            options.errorRef.current.focus();
          }
        });

        if (import.meta.env.DEV) {
          console.error('Form submission error:', error);
        }

        if (onError && error instanceof Error) {
          onError(error);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [url, schema, onSuccess, onError, conversionLabel, conversionValue, leadEventName, resetDelay, options.formRef, options.errorRef, options.successRef]
  );

  return {
    status,
    handleSubmit,
    reset,
    isSubmitting: status === 'submitting',
    isSuccess: status === 'success',
    isError: status === 'error',
    validationErrors,
    hasValidationErrors: Object.keys(validationErrors).length > 0,
  };
}